import { Header, RowValue } from 'bam-node-savant-table/table-ts.ts'
import { guff } from 'bam-node-baseball-savant/utils/guff'
import getGroupedHeaders from '../utils/get-grouped-headers'
import {
    AbsChallengeRow,
    AbsChallengeDrawerRow,
    ServerParams,
} from '../swing-path/types/globals.ts'
import {
    playerDisplayFnByIdLink,
    teamDisplayFnWithLogo,
} from '../utils/bat-tracking-table-helpers'

// Globals
declare const absData: AbsChallengeRow[]
// declare const selectedView: string
declare const serverParams: ServerParams
// declare const isProd: boolean

type Mode = 'for' | 'against' | 'forcatchers' | 'againstcatchers'
type Count = 'chal' | 'runs'

const playerTypes = ['batter', 'catcher', 'pitcher']
const offensiveTypes = ['batter', 'batting-team']
// const defensiveTypes = ['catcher', 'pitcher', 'catching-team']
const selectorId = 'abs-mode-selector'
const countSelectorId = 'abs-count-selector'

const isPlayerType = playerTypes.includes(serverParams.challengeType || '')

const SUCCESS_CHAL_COLOR = '#399098'
const FAIL_CHAL_COLOR = '#444'
// const isOffensiveType = offensiveTypes.includes(
//     String(serverParams.challengeType)
// )
// const isDefensiveType = defensiveTypes.includes(
//     String(serverParams.challengeType)
// )
// const isSummaryType = serverParams.challengeType === 'team-summary'

export const groupHeaders = getGroupedHeaders(serverParams.secondaryGroupBy)

export function getSingleChallengeHeaders(buttonType: Mode) {
    // const isMinors = serverParams.level !== 'mlb'

    const singleChallengeHeaders: Header<AbsChallengeDrawerRow>[] = [
        {
            name: 'Game Date',
            field: 'game_date',
            headerClass: `th-component-header align-left th-vertical-bottom table-static-column`,
            fieldClass: `tr-data align-left table-static-column`,
            defaultSortDir: 'asc',
            displayFn: (val) =>
                typeof val === 'string' ? val?.split('T')[0] : '',
        },
        {
            name: 'Inn.',
            field: 'event_inning',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
        },
        {
            name: 'Count',
            field: 'pre_ball_count',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'asc',
            displayFn: (val, row) => `${val}-${row.pre_strike_count}`,
        },
        {
            name: 'Batter',
            field: 'batter_name',
            headerClass: `th-component-header align-left th-vertical-bottom`,
            fieldClass: `tr-data align-left`,
            defaultSortDir: 'asc',
            displayFn: (
                val,
                {
                    player_at_bat,
                    is_challengeABS_overturned,
                    challenging_player_id,
                }
            ) =>
                playerDisplayFnByIdLink(
                    String(val ?? ''),
                    player_at_bat,
                    '?stats=statcast-r-abs-mlb#statcast_nav_type_abs',
                    {
                        firstInit: true,
                        chalFormat: challenging_player_id === player_at_bat,
                        chalSuccess: Boolean(is_challengeABS_overturned),
                    }
                ),
        },
        {
            name: 'Bat<br />Side',
            field: 'bat_side',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
        },
        {
            name: 'Bat<br />Team',
            field: 'bat_team_abbr',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'asc',
            displayFn: (val, { bat_team_id }) => {
                if (serverParams.level === 'mlb') {
                    return teamDisplayFnWithLogo(String(val), bat_team_id, {
                        showName: false,
                    })
                }
                return String(val)
            },
        },
        {
            name: 'Catcher',
            field: 'catcher_name',
            headerClass: `th-component-header align-left th-vertical-bottom`,
            fieldClass: `tr-data align-left`,
            defaultSortDir: 'asc',
            displayFn: (
                val,
                { fielder_2, is_challengeABS_overturned, challenging_player_id }
            ) =>
                playerDisplayFnByIdLink(
                    String(val ?? ''),
                    fielder_2,
                    '?stats=statcast-r-abs-mlb#statcast_nav_type_abs',
                    {
                        firstInit: true,
                        chalFormat: fielder_2 === challenging_player_id,
                        chalSuccess: Boolean(is_challengeABS_overturned),
                    }
                ),
        },
        {
            name: 'Pitcher',
            field: 'pitcher_name',
            headerClass: `th-component-header align-left th-vertical-bottom`,
            fieldClass: `tr-data align-left`,
            defaultSortDir: 'asc',
            displayFn: (
                val,
                { pitcher, is_challengeABS_overturned, challenging_player_id }
            ) =>
                playerDisplayFnByIdLink(
                    String(val ?? ''),
                    pitcher,
                    '?stats=statcast-r-abs-mlb#statcast_nav_type_abs',
                    {
                        firstInit: true,
                        chalFormat: pitcher === challenging_player_id,
                        chalSuccess: Boolean(is_challengeABS_overturned),
                    }
                ),
        },
        {
            name: 'Fld.<br />Team',
            field: 'fld_team_abbr',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'asc',
            displayFn: (val, { fld_team_id }) => {
                if (serverParams.level === 'mlb') {
                    return teamDisplayFnWithLogo(String(val), fld_team_id, {
                        showName: false,
                    })
                }
                return String(val)
            },
        },
        {
            name: 'Chal.<br />Prob.',
            field: 'sz_challenge_prob',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
        },
        {
            name: 'Edge<br />Dist.<br />(in.)',
            field: 'edge_dist_calc',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'asc',
            toolTip: `Red values are<br />are on overturns,<br />blue values are<br />on confirmed calls`,
            displayFn: (val: RowValue, row: AbsChallengeDrawerRow) => {
                let guffedVal

                guffedVal = guff(Math.abs(Number(val)), 'edge_dist')
                if (Math.abs(Number(val)) > 3) {
                    guffedVal = '> 3'
                }
                if (guffedVal === '0.0' && Number(val) !== 0) {
                    guffedVal = '< 0.1'
                }

                const color = row.is_challengeABS_overturned
                    ? SUCCESS_CHAL_COLOR
                    : FAIL_CHAL_COLOR

                return `<span style="color: ${color};">${guffedVal}</span>`
            },
        },
        {
            name: 'Chal.<br />Result',
            field: 'is_challengeABS_overturned',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val) =>
                val === 0
                    ? `<span class="unsuccessful-challenge">✖</span>`
                    : '<span class="successful-challenge" style="font-size: 1.5em; line-height: 0; display: inline-block;">↺</span>',
        },
        {
            name: 'K<br />Flip',
            field: `is_strikeout_overturn${
                buttonType.includes('against') ? '_against' : ''
            }`,
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
            fieldClass: `tr-data align-center dashed-left`,
            defaultSortDir: 'asc',
            displayFn: (val) =>
                val === 0
                    ? ''
                    : '<span class="successful-challenge" style="font-size: 1.5em; line-height: 0; display: inline-block;">↺</span>',
        },
        {
            name: 'BB<br />Flip',
            field: `is_walk_overturn${
                buttonType.includes('against') ? '_against' : ''
            }`,
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val) =>
                val === 0
                    ? ''
                    : '<span class="successful-challenge" style="font-size: 1.5em; line-height: 0; display: inline-block;">↺</span>',
        },
        {
            name: 'Chal.<br />Runs',
            field: `sz_challenge_runs`,
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'asc',
            displayFn: (val, row) => {
                const color = row.is_challengeABS_overturned
                    ? SUCCESS_CHAL_COLOR
                    : FAIL_CHAL_COLOR

                return `<span style="color: ${color};">${guff(
                    val,
                    'abs_chal_runs',
                    { isDiff: true }
                )}</span>`
            },
        },
        {
            name: 'Is<br />Rsn.',
            field: `is_challengeABS_reasonable_attempt`,
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val) =>
                val === 0 ? '' : '<span class="successful-challenge">✔</span>',
        },
    ]

    // if (!isMinors) {
    //     singleChallengeHeaders.push({
    //         name: '',
    //         field: 'play_id',
    //         headerClass:
    //             'th-component-header align-center th-vertical-bottom video-col cursor-initial',
    //         fieldClass: 'tr-data video-col',
    //         type: 'number',
    //         sortable: false,
    //         displayFn: (val) => `
    //                 <a href="/sporty-videos?playId=${val}" target="_BLANK">
    //                     <img src="/site-core/images/video.png" style="margin-right: 4px; width: 25px; height: 13px; cursor: pointer;">
    //                 </a>
    //                `,
    //     })
    // }

    // if (!isProd) {
    //     singleChallengeHeaders.push({
    //         name: '',
    //         field: 'play_id',
    //         headerClass:
    //             'th-component-header align-center th-vertical-bottom video-col cursor-initial',
    //         fieldClass: 'tr-data video-col',
    //         type: 'number',
    //         sortable: false,
    //         displayFn: (val, row) => `
    //                 <a href="https://research.mlb.com/games/${row.game_pk}/plays/${val}" target="_BLANK">
    //                     ⓘ
    //                 </a>
    //                `,
    //     })
    // }

    return singleChallengeHeaders
}

function handlePlayerTeam(abbrev: string, n_teams: number) {
    if (n_teams === 1) {
        return abbrev
    }
    return `${n_teams} Tms`
}

export function getPitcherHeaders(level: string) {
    const headers: Header<AbsChallengeRow>[] = [
        {
            name: `Player`,
            field: 'player_name',
            headerClass: `th-component-header align-left th-vertical-bottom table-static-column`,
            fieldClass: `tr-data align-left table-static-column`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                playerDisplayFnByIdLink(
                    String(val ?? ''),
                    row.id,
                    '?stats=statcast-r-abs-mlb#statcast_nav_type_abs'
                ),
        },
        {
            name: `Team`,
            field: 'team_abbr',
            headerClass: `th-component-header align-center th-vertical-bottom ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                handlePlayerTeam(String(val), Number(row.n_teams)),
        },
        {
            name: 'Level',
            field: 'level',
            headerClass: `th-component-header align-center th-vertical-bottom ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            defaultSortDir: 'asc',
            displayFn: (val, row) => {
                if (row.n_levels > 1) {
                    return `${row.n_levels} Lvls`
                }
                return String(val ?? '')
            },
        },
        {
            name: `${level === 'mlb' ? 'Team' : 'Org'}`,
            field: 'parent_org',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                teamDisplayFnWithLogo(String(val ?? ''), row.parent_org_code, {
                    showName: false,
                }),
        },
        {
            name: 'Chal',
            field: 'n_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Challenges made<br />by pitcher`,
        },
        {
            name: 'Won',
            field: 'n_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Calls overturned on<br />challenges by pitcher`,
        },
        {
            name: 'Lost',
            field: 'n_fails',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for`,
            fieldClass: `tr-data align-center abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Calls confirmed on<br />challenges by pitcher`,
        },
        {
            name: 'Won%',
            field: 'rate_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of calls overturned on<br />challenges by pitcher`,
        },
        {
            name: `+K<br />Flip`,
            field: 'n_strikeouts',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Strikeouts<br />gained by<br />challenges`,
        },
        {
            name: `-BB<br />Flip`,
            field: 'n_walks',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-for`,
            fieldClass: `tr-data align-center border-right abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Walks<br />overturned by<br />challenges`,
        },
        {
            name: 'Chal.<br />Rate',
            field: 'rate_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for`,
            fieldClass: `tr-data align-center abs-chal-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `% of "challengeable"<br />pitches challenged<br />by pitcher`,
        },
    ]

    return headers
}

export function getUmpireHeaders() {
    const headers: Header<AbsChallengeRow>[] = [
        {
            name: `Umpire`,
            field: 'umpire_name',
            headerClass: `th-component-header align-left th-vertical-bottom`,
            fieldClass: `tr-data align-left`,
            defaultSortDir: 'asc',
        },
        {
            name: `ID`,
            field: 'umpire_id',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
        },
        {
            name: `Takes`,
            field: 'n_total_sample',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
        },
        {
            name: 'Net<br />For',
            field: 'net_net_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
            highlightDynamic: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
        },
        {
            name: 'Chal',
            field: 'n_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
        },
        {
            name: 'Won',
            field: 'n_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
            fieldClass: `tr-data align-center dashed-left`,
            defaultSortDir: 'desc',
        },
        {
            name: 'Lost',
            field: 'n_fails',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
        },
        {
            name: 'Won%',
            field: 'rate_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
            fieldClass: `tr-data align-center dashed-left`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
        },
        {
            name: 'xChal',
            field: 'exp_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
        },
        {
            name: 'xWon',
            field: 'exp_chal_gained',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
            fieldClass: `tr-data align-center dashed-left`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
        },
        {
            name: 'xLost',
            field: 'exp_chal_lost',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
        },
        {
            name: 'xWon%',
            field: 'exp_rate_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
            fieldClass: `tr-data align-center dashed-left`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_percent'),
        },
        {
            name: 'Won<br />vs. Exp.',
            field: 'net_chal_gained',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals', { isDiff: true }),
        },
        {
            name: 'Lost<br />vs. Exp.',
            field: 'net_chal_lost',
            type: 'number',
            headerClass: `th-component-header align-center border-right th-vertical-bottom`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals', { isDiff: true }),
        },
        {
            name: `K<br />Flip`,
            field: 'n_strikeouts',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
        },
        {
            name: `BB<br />Flip`,
            field: 'n_walks',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'desc',
        },
        {
            name: 'Chal.<br />Rate',
            field: 'rate_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
        },
        {
            name: 'xChal.<br />Rate',
            field: 'exp_rate_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
        },
        {
            name: 'Rate<br />vs. Exp.',
            field: 'exp_rate_challenges_diff',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-left border-right`,
            defaultSortDir: 'desc',
            displayFn: (val) =>
                guff(val, 'abs_chal_rate_percent', {
                    isDiff: true,
                    ignoreExtremaHandling: true,
                }),
            highlightDynamic: true,
        },
        {
            name: 'Shad.<br />Acc.',
            field: 'shadow_correct_rate',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
        },
        {
            name: 'In<br />Acc.',
            field: 'in_zone_correct_rate',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
        },
        {
            name: 'Out<br />Acc.',
            field: 'out_zone_correct_rate',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
        },
        {
            name: 'Ovr.<br />Acc.',
            field: 'rate_correct',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left`,
            fieldClass: `tr-data align-center dashed-left`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
        },
    ]

    return headers
}

export function getHeaders(level: string) {
    const entityType = isPlayerType ? 'player' : 'team'
    const strikeoutDelta = offensiveTypes.includes(
        String(serverParams.challengeType)
    )
        ? 'eliminated'
        : 'gained'
    const walkDelta = offensiveTypes.includes(
        String(serverParams.challengeType)
    )
        ? 'gained'
        : 'eliminated'
    const strikeoutAgainstDelta = offensiveTypes.includes(
        String(serverParams.challengeType)
    )
        ? 'gained'
        : 'eliminated'
    const walkAgainstDelta = offensiveTypes.includes(
        String(serverParams.challengeType)
    )
        ? 'eliminated'
        : 'gained'

    const headers: Header<AbsChallengeRow>[] = [
        {
            name: `${isPlayerType ? 'Player' : 'Team'}`,
            field: 'player_name',
            headerClass: `th-component-header align-left th-vertical-bottom table-static-column`,
            fieldClass: `tr-data align-left table-static-column`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                isPlayerType
                    ? playerDisplayFnByIdLink(
                          String(val ?? ''),
                          row.id,
                          '?stats=statcast-r-abs-mlb#statcast_nav_type_abs'
                      )
                    : String(val),
        },
        {
            name: `${isPlayerType ? 'Team' : ''}`,
            field: 'team_abbr',
            headerClass: `th-component-header align-center th-vertical-bottom ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                isPlayerType
                    ? handlePlayerTeam(String(val), Number(row.n_teams))
                    : teamDisplayFnWithLogo(String(val ?? ''), row.id, {
                          showName: false,
                          padding: true,
                      }),
        },
        {
            name: 'Level',
            field: 'level',
            headerClass: `th-component-header align-center th-vertical-bottom ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            defaultSortDir: 'asc',
            displayFn: (val, row) => {
                if (row.n_levels > 1) {
                    return `${row.n_levels} Lvls`
                }
                return String(val ?? '')
            },
        },
        {
            name: `${level === 'mlb' ? 'Team' : 'Org'}`,
            field: 'parent_org',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                teamDisplayFnWithLogo(String(val ?? ''), row.parent_org_code, {
                    showName: false,
                    padding: true,
                }),
        },
        {
            name: 'Total vs.<br />Expected',
            field: 'overturns_vs_exp',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for-expanded abs-chal-against-expanded`,
            fieldClass: `tr-data align-center border-left abs-chal-for-expanded abs-chal-against-expanded`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            highlightDynamic: true,
            toolTip: `Net overturns more<br />than expected by<br />a ${entityType} seeing identical<br />pitches (incl. challenges<br />by and against ${entityType})`,
        },
        {
            name: 'Net<br />Ovr.<br />For',
            field: 'net_net_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for abs-chal-for-expanded abs-runs-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for abs-chal-for-expanded abs-runs-for`,
            defaultSortDir: 'desc',
            highlightDynamic: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Net ABS overturns more<br />than expected by<br />a ${entityType} seeing identical<br />pitches (only challenges<br />by ${entityType})`,
        },
        {
            name: 'Net<br />Ovr.<br />Against',
            field: 'net_net_chal_against_proxy',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left border-right abs-chal-against abs-chal-against-expanded abs-runs-against`,
            fieldClass: `tr-data align-center border-left border-right abs-chal-against abs-chal-against-expanded abs-runs-against`,
            defaultSortDir: 'desc',
            highlightDynamic:
                absData
                    ?.map((d) => d.net_net_chal_against_proxy)
                    ?.findIndex((d) => d !== null) !== -1,
            displayFn: (val, row) =>
                guff(row.net_net_chal_against, 'exp_abs_chals'),
            toolTip: `Net overturns more<br />than expected by<br />a ${entityType} seeing identical<br />pitches (only challenges<br />against ${entityType})`,
        },
        {
            name: 'Total vs.<br />Expected',
            field: 'runs_vs_exp',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-for-expanded abs-runs-against-expanded ${
                !isPlayerType ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center border-left abs-runs-for-expanded abs-runs-against-expanded ${
                !isPlayerType ? 'hidden' : ''
            }`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            highlightDynamic: true,
            toolTip: `Net run value more<br />than expected by<br />a ${entityType} seeing identical<br />pitches (incl. challenges<br />by and against ${entityType})`,
        },
        {
            name: isPlayerType ? 'Net Runs<br />For' : 'Gained<br />vs. Exp.',
            field: isPlayerType ? 'net_net_runs' : 'net_chal_gained_runs',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for abs-runs-for ${
                isPlayerType ? 'abs-runs-for-expanded' : ''
            }`,
            fieldClass: `tr-data align-center border-left abs-chal-for abs-runs-for ${
                isPlayerType ? 'abs-runs-for-expanded' : ''
            }`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            highlightDynamic: true,
            toolTip: isPlayerType
                ? `Net run value more<br />than expected by<br />a ${entityType} seeing identical<br />pitches (incl. challenges<br />by and against ${entityType})`
                : `Actual run value on<br />overturns on challenges by<br />${entityType} minus expected`,
        },
        {
            name: isPlayerType
                ? 'Net Runs<br />Against'
                : 'Gained<br />vs. Exp.',
            field: isPlayerType
                ? 'net_net_runs_against_proxy'
                : 'net_chal_gained_runs_against_proxy',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left border-right abs-runs-against abs-chal-against ${
                isPlayerType ? 'abs-runs-against-expanded' : ''
            }`,
            fieldClass: `tr-data align-center border-left border-right abs-runs-against abs-chal-against ${
                isPlayerType ? 'abs-runs-against-expanded' : ''
            }`,
            defaultSortDir: 'desc',
            highlightDynamic: () => {
                if (isPlayerType) {
                    return (
                        absData
                            ?.map((d) => d.net_net_runs_against_proxy)
                            ?.findIndex((d) => d !== null) !== -1
                    )
                }
                return (
                    absData
                        ?.map((d) => d.net_chal_gained_runs_against_proxy)
                        ?.findIndex((d) => d !== null) !== -1
                )
            },
            displayFn: (val, row) => {
                if (isPlayerType) {
                    return guff(row.net_net_runs_against, 'abs_chal_runs')
                }
                return guff(
                    row.net_chal_gained_runs_against_proxy,
                    'abs_chal_runs'
                )
            },
            toolTip: isPlayerType
                ? `Net run value more<br />than expected by<br />a ${entityType} seeing identical<br />pitches (only challenges<br />against ${entityType})`
                : `Actual run value on<br />overturns on challenges against<br />${entityType} minus expected`,
        },
        {
            name: 'Chal',
            field: 'n_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Challenges made<br />by ${entityType}`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won',
            field: 'n_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Calls overturned on<br />challenges by ${entityType}`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Lost',
            field: 'n_fails',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for`,
            fieldClass: `tr-data align-center abs-chal-for`,
            defaultSortDir: 'desc',
            toolTip: `Calls confirmed on<br />challenges by ${entityType}`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won%',
            field: 'rate_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of calls overturned on<br />challenges by ${entityType}`,
        },
        {
            name: isPlayerType ? 'Chal.<br />Runs' : 'Chal.',
            field: isPlayerType ? 'n_chal_runs' : 'n_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-for`,
            fieldClass: `tr-data align-center border-left abs-runs-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => {
                if (isPlayerType) {
                    return guff(val, 'abs_chal_runs')
                }
                return String(val)
            },
            toolTip: isPlayerType
                ? `Run value on<br />challenges made<br />by ${entityType}`
                : `Challenges made<br />by ${entityType}`,
        },
        {
            name: 'Runs<br />Gained',
            field: 'n_chal_runs_gained',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-runs-for`,
            fieldClass: `tr-data align-center dashed-left abs-runs-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Run value on<br />calls overturned on<br />challenges by ${entityType}`,
        },
        {
            name: isPlayerType
                ? 'Runs<br />Lost'
                : 'Runs<br />Gained<br />Per Chal.',
            field: isPlayerType ? 'n_chal_runs_lost' : 'runs_gained_per_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-runs-for`,
            fieldClass: `tr-data align-center abs-runs-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => {
                if (isPlayerType) {
                    return guff(val, 'abs_chal_runs')
                }
                return guff(val, 'runs_gained_per_chal')
            },
            toolTip: isPlayerType
                ? `Run value on<br />calls confirmed on<br />challenges by ${entityType}`
                : `Run value on<br />calls overturned per<br />challenge by ${entityType}`,
        },
        {
            name: 'xChal',
            field: 'exp_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Expected number of<br />challenges by ${entityType}<br />on identical pitches`,
        },
        {
            name: 'xWon',
            field: 'exp_chal_gained',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Expected number of<br />overturns on challenges by<br />${entityType} on identical pitches`,
        },
        {
            name: 'xLost',
            field: 'exp_chal_lost',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for`,
            fieldClass: `tr-data align-center abs-chal-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Expected number of<br />calls confirmed on challenges<br />by ${entityType} on identical pitches`,
        },
        {
            name: 'xWon%',
            field: 'exp_rate_overturns',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `Expected % of calls<br />overturned on challenges by<br />${entityType} on identical pitches`,
        },
        {
            name: isPlayerType ? 'xChal.<br />Runs' : 'xChal.',
            field: isPlayerType ? 'exp_chal_runs' : 'exp_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-for`,
            fieldClass: `tr-data align-center border-left abs-runs-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: isPlayerType
                ? `Expected run value<br />on challenges by<br />${entityType} on identical pitches`
                : `Expected number of<br />challenges by ${entityType}<br />on identical pitches`,
        },
        {
            name: 'xRuns<br />Gained',
            field: 'exp_chal_runs_gained',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-runs-for`,
            fieldClass: `tr-data align-center dashed-left abs-runs-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Expected run value on<br />overturns on challenges by<br />${entityType} on identical pitches`,
        },
        {
            name: isPlayerType
                ? 'xRuns<br />Lost'
                : 'xRuns<br />Gained<br />Per Chal.',
            field: isPlayerType
                ? 'exp_chal_runs_lost'
                : 'exp_runs_gained_per_chal',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-runs-for`,
            fieldClass: `tr-data align-center abs-runs-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => {
                if (isPlayerType) {
                    return guff(val, 'abs_chal_runs')
                }
                return guff(val, 'runs_gained_per_chal')
            },
            toolTip: isPlayerType
                ? `Expected run value on<br />calls confirmed on challenges<br />by ${entityType} on identical pitches`
                : `Expected Run value<br />on calls overturned per<br />challenge by ${entityType}`,
        },
        {
            name: 'Won<br />vs. Exp.',
            field: 'net_chal_gained',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals', { isDiff: true }),
            toolTip: `Actual number of calls<br />overturned on challenges by<br />${entityType} minus expected<br />(Won - xWon)`,
        },
        {
            name: 'Lost<br />vs. Exp.',
            field: 'net_chal_lost',
            type: 'number',
            headerClass: `th-component-header align-center border-right th-vertical-bottom abs-chal-for`,
            fieldClass: `tr-data align-center border-right abs-chal-for`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals', { isDiff: true }),
            toolTip: `Actual number of calls<br />confirmed on challenges by<br />${entityType} minus expected<br />(Lost - xLost)`,
        },
        {
            name: 'Gained<br />vs. Exp.',
            field: 'net_chal_gained_runs',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-for ${
                !isPlayerType ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center border-left abs-runs-for ${
                !isPlayerType ? 'hidden' : ''
            }`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs', { isDiff: true }),
            toolTip: `Actual run value on<br />overturns on challenges by<br />${entityType} minus expected<br />(Won - xWon)`,
        },
        {
            name: 'Lost<br />vs. Exp.',
            field: 'net_chal_lost_runs',
            type: 'number',
            headerClass: `th-component-header align-center border-right th-vertical-bottom abs-runs-for ${
                !isPlayerType ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center border-right abs-runs-for ${
                !isPlayerType ? 'hidden' : ''
            }`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs', { isDiff: true }),
            toolTip: `Actual run value on<br />confirms on challenges by<br />${entityType} minus expected<br />(Lost - xLost)`,
        },
        {
            name: `${
                offensiveTypes.includes(serverParams.challengeType || '')
                    ? '-'
                    : '+'
            }K<br />Flip`,
            field: 'n_strikeouts',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center border-left abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            toolTip: `Strikeouts<br />${strikeoutDelta} by<br />challenges`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: `${
                offensiveTypes.includes(serverParams.challengeType || '')
                    ? '+'
                    : '-'
            }BB<br />Flip`,
            field: 'n_walks',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center border-right abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            toolTip: `Walks<br />${walkDelta} by<br />challenges`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Chal.<br />Rate',
            field: 'rate_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `% of "challengeable"<br />pitches challenged<br />by ${entityType}`,
        },
        {
            name: 'xChal.<br />Rate',
            field: 'exp_rate_challenges',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `Expected % of<br />"challengeable"<br />pitches challenged<br />by ${entityType}`,
        },
        {
            name: 'Rate<br />vs. Exp.',
            field: 'exp_rate_challenges_diff',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center border-left border-right abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            displayFn: (val) =>
                guff(val, 'abs_chal_rate_percent', {
                    isDiff: true,
                    ignoreExtremaHandling: true,
                }),
            highlightDynamic: true,
            toolTip: `Actual - expected %<br />of "challengeable"<br />pitches challenged<br />by ${entityType}`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
        },
        {
            name: 'Rsn.<br />Opp.',
            field: 'n_chal_reasonable_opps',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            toolTip: `Opportunities for<br />challenges by ${entityType}<br />considered "reasonable"`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Rsn.<br />Chal.',
            field: 'n_chal_reasonable',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            toolTip: `# of challenges<br />by ${entityType} considered<br />"reasonable"`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: '% Chal.<br />Rsn.',
            field: 'rate_chal_reasonable',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center dashed-left abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            // displayFn: (val, row) => {
            //     if (!row.n_challenges) {
            //         return '--'
            //     }

            //     return `${row.n_chal_reasonable} / ${row.n_challenges}`
            // },
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of challenges<br />by ${entityType} considered<br />"reasonable"`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
        },
        {
            name: '% Rsn.<br />Taken',
            field: 'rate_reasonable_opp_taken',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-for abs-runs-for`,
            fieldClass: `tr-data align-center border-right abs-chal-for abs-runs-for`,
            defaultSortDir: 'desc',
            // displayFn: (val, row) => {
            //     if (!row.n_chal_reasonable_opps) {
            //         return '--'
            //     }

            //     return `${row.n_chal_reasonable} / ${row.n_chal_reasonable_opps}`
            // },
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of "reasonable"<br />challenge opps. taken<br />by ${entityType}`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
        },
        {
            name: 'Chal',
            field: 'n_challenges_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-against`,
            fieldClass: `tr-data align-center border-left abs-chal-against`,
            defaultSortDir: 'desc',
            toolTip: `Challenges made<br />against ${entityType}`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won',
            field: 'n_overturns_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-against`,
            fieldClass: `tr-data align-center dashed-left abs-chal-against`,
            defaultSortDir: 'desc',
            toolTip: `Calls overturned on<br />challenges against ${entityType}`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Lost',
            field: 'n_fails_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-against`,
            fieldClass: `tr-data align-center abs-chal-against`,
            defaultSortDir: 'desc',
            toolTip: `Calls confirmed on<br />challenges against ${entityType}`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won%',
            field: 'rate_overturns_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-against`,
            fieldClass: `tr-data align-center dashed-left abs-chal-against`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of calls overturned on<br />challenges against ${entityType}`,
        },
        {
            name: isPlayerType ? 'Chal.<br />Runs' : 'Chal.',
            field: isPlayerType
                ? 'n_chal_runs_against'
                : 'n_challenges_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-against`,
            fieldClass: `tr-data align-center border-left abs-runs-against`,
            defaultSortDir: 'desc',
            displayFn: (val) => {
                if (isPlayerType) {
                    return guff(val, 'abs_chal_runs')
                }
                return String(val)
            },
            toolTip: isPlayerType
                ? `Run value on<br />challenges made<br />against ${entityType}`
                : `Challenges made<br />against ${entityType}`,
        },
        {
            name: 'Runs<br />Gained',
            field: 'n_chal_runs_gained_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-runs-against`,
            fieldClass: `tr-data align-center dashed-left abs-runs-against`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Run value on<br />calls overturned on<br />challenges against ${entityType}`,
        },
        {
            name: isPlayerType
                ? 'Runs<br />Lost'
                : 'Runs<br />Gained<br />Per Chal.',
            field: isPlayerType
                ? 'n_chal_runs_lost_against'
                : 'runs_gained_per_chal_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-runs-against`,
            fieldClass: `tr-data align-center abs-runs-against`,
            defaultSortDir: 'desc',
            displayFn: (val) => {
                if (isPlayerType) {
                    return guff(val, 'abs_chal_runs')
                }
                return guff(val, 'runs_gained_per_chal')
            },
            toolTip: isPlayerType
                ? `Run value on<br />calls confirmed on<br />challenges against ${entityType}`
                : `Run value on<br />calls overturned per<br />challenge against ${entityType}`,
        },
        {
            name: 'xChal',
            field: 'exp_chal_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-against`,
            fieldClass: `tr-data align-center border-left abs-chal-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Expected number of<br />challenges against ${entityType}<br />on identical pitches`,
        },
        {
            name: 'xWon',
            field: 'exp_chal_gained_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-against`,
            fieldClass: `tr-data align-center dashed-left abs-chal-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Expected number of<br />overturns on challenges against<br />${entityType} on identical pitches`,
        },
        {
            name: 'xLost',
            field: 'exp_chal_lost_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-against`,
            fieldClass: `tr-data align-center abs-chal-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Expected number of<br />calls confirmed on challenges<br />against ${entityType} on identical pitches`,
        },
        {
            name: 'xWon%',
            field: 'exp_rate_overturns_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-against`,
            fieldClass: `tr-data align-center dashed-left abs-chal-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `Expected % of calls<br />overturned on challenges against<br />${entityType} on identical pitches`,
        },
        {
            name: isPlayerType ? 'xChal.<br />Runs' : 'xChal.',
            field: isPlayerType ? 'exp_chal_runs_against' : 'exp_chal_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-against`,
            fieldClass: `tr-data align-center border-left abs-runs-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: isPlayerType
                ? `Expected run value<br />on challenges against<br />${entityType} on identical pitches`
                : `Expected number of<br />challenges against ${entityType}<br />on identical pitches`,
        },
        {
            name: 'xRuns<br />Gained',
            field: 'exp_chal_runs_gained_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-runs-against`,
            fieldClass: `tr-data align-center dashed-left abs-runs-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Expected run value on<br />overturns on challenges against<br />${entityType} on identical pitches`,
        },
        {
            name: isPlayerType
                ? 'xRuns<br />Lost'
                : 'xRuns<br />Gained<br />Per Chal.',
            field: isPlayerType
                ? 'exp_chal_runs_lost_against'
                : 'exp_runs_gained_per_chal_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-runs-against`,
            fieldClass: `tr-data align-center abs-runs-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => {
                if (isPlayerType) {
                    return guff(val, 'abs_chal_runs')
                }
                return guff(val, 'runs_gained_per_chal')
            },
            toolTip: isPlayerType
                ? `Expected run value on<br />confirms on challenges by<br />${entityType} on identical pitches`
                : `Expected Run value<br />on calls overturned per<br />challenge against ${entityType}`,
        },
        {
            name: 'Won<br />vs. Exp.',
            field: 'net_chal_gained_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-against`,
            fieldClass: `tr-data align-center border-left abs-chal-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals', { isDiff: true }),
            toolTip: `Actual number of calls<br />overturned on challenges against<br />${entityType} minus expected<br />(Won - xWon)`,
        },
        {
            name: 'Lost<br />vs. Exp.',
            field: 'net_chal_lost_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-against`,
            fieldClass: `tr-data align-center border-right abs-chal-against`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals', { isDiff: true }),
            toolTip: `Actual run value on<br />confirms on challenges against<br />${entityType} minus expected<br />(Lost - xLost)`,
        },
        {
            name: 'Gained<br />vs. Exp.',
            field: 'net_chal_gained_runs_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-runs-against ${
                !isPlayerType ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center border-left abs-runs-against ${
                !isPlayerType ? 'hidden' : ''
            }`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs', { isDiff: true }),
            toolTip: `Actual run value on<br />overturns on challenges against<br />${entityType} minus expected<br />(Won - xWon)`,
        },
        {
            name: 'Lost<br />vs. Exp.',
            field: 'net_chal_lost_runs_against',
            type: 'number',
            headerClass: `th-component-header align-center border-right th-vertical-bottom abs-runs-against ${
                !isPlayerType ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center border-right abs-runs-against ${
                !isPlayerType ? 'hidden' : ''
            }`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs', { isDiff: true }),
            toolTip: `Actual number of calls<br />confirmed on challenges by<br />${entityType} minus expected<br />(Lost - xLost)`,
        },
        {
            name: `${
                offensiveTypes.includes(serverParams.challengeType || '')
                    ? '+'
                    : '-'
            }K<br />Flip`,
            field: 'n_strikeouts_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center border-left abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            toolTip: `Strikeouts<br />${strikeoutAgainstDelta} against<br />${entityType} by<br />challenges`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: `${
                offensiveTypes.includes(serverParams.challengeType || '')
                    ? '-'
                    : '+'
            }BB<br />Flip`,
            field: 'n_walks_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center border-right abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            toolTip: `Walks ${walkAgainstDelta}<br /> against ${entityType} by<br />challenges`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Chal.<br />Rate',
            field: 'rate_challenges_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `% of "challengeable"<br />pitches challenged<br />against ${entityType}`,
        },
        {
            name: 'xChal.<br />Rate',
            field: 'exp_rate_challenges_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `Expected % of<br />"challengeable"<br />pitches challenged<br />against ${entityType}`,
        },
        {
            name: 'Rate<br />vs. Exp.',
            field: 'exp_rate_challenges_diff_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center border-left border-right abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            displayFn: (val) =>
                guff(val, 'abs_chal_rate_percent', {
                    isDiff: true,
                    ignoreExtremaHandling: true,
                }),
            highlightDynamic: true,
            toolTip: `Actual - expected %<br />of "challengeable"<br />pitches challenged<br />against ${entityType}`,
        },
        {
            name: 'Rsn.<br />Opp.',
            field: 'n_chal_reasonable_opps_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            toolTip: `Opportunities for<br />challenges against ${entityType} considered<br />"reasonable"`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Rsn.<br />Chal.',
            field: 'n_chal_reasonable_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            toolTip: `# of challenges<br />against ${entityType} considered<br />"reasonable"`,
            toolTipClass: 'th-tooltip th-tooltip-left-bump',
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: '% Chal<br />Rsn.',
            field: 'rate_chal_reasonable_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center dashed-left abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            // displayFn: (val, row) => {
            //     if (!row.n_challenges_against) {
            //         return '--'
            //     }

            //     return `${row.n_chal_reasonable_against} / ${row.n_challenges_against}`
            // },
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of challenges<br />against ${entityType}<br />considered "reasonable"`,
        },
        {
            name: '% Rsn.<br />Taken',
            field: 'rate_reasonable_opp_taken_against',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-chal-against abs-runs-against`,
            fieldClass: `tr-data align-center border-right abs-chal-against abs-runs-against`,
            defaultSortDir: 'desc',
            // displayFn: (val, row) => {
            //     if (!row.n_chal_reasonable_opps_against) {
            //         return '--'
            //     }

            //     return `${row.n_chal_reasonable_against} / ${row.n_chal_reasonable_opps_against}`
            // },
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of "reasonable"<br />challenge opps. taken<br />by ${entityType}`,
        },
        {
            name: '',
            field: 'player_name',
            headerClass:
                'th-component-header align-center th-vertical-bottom cursor-initial',
            fieldClass: 'tr-data',
            sortable: false,
            displayFn: () =>
                `
                    <span data-video-icon style="inline-block; padding: 2px; border-radius: 50%; border: 2px solid #c2c2c2;">
                        <img src="/site-core/images/video.png" style="width: 18px; cursor: pointer;">
                    </span>
                    `,
        },
    ]

    return headers
}

export function getHeadersSummary(isExpanded: boolean, level: string) {
    const headersSummary: Header<AbsChallengeRow>[] = [
        {
            name: 'Team',
            field: 'player_name',
            headerClass: `th-component-header align-left th-vertical-bottom table-static-column`,
            fieldClass: `tr-data align-left table-static-column`,
            defaultSortDir: 'asc',
        },
        // {
        //     name: '',
        //     field: 'team_abbr',
        //     headerClass: `th-component-header align-center th-vertical-bottom`,
        //     fieldClass: `tr-data align-center`,
        //     defaultSortDir: 'asc',
        //     displayFn: (val, row) =>
        //         teamDisplayFnWithLogo(String(val ?? ''), row.id, {
        //             showName: false,
        //         }),
        // },
        {
            name: 'Level',
            field: 'level',
            headerClass: `th-component-header align-center th-vertical-bottom ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            fieldClass: `tr-data align-center ${
                level === 'mlb' ? 'hidden' : ''
            }`,
            defaultSortDir: 'asc',
        },
        {
            name: `${level === 'mlb' ? '' : 'Org'}`,
            field: 'parent_org',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'asc',
            displayFn: (val, row) =>
                teamDisplayFnWithLogo(String(val ?? ''), row.parent_org_code, {
                    showName: false,
                    padding: true,
                }),
        },
        {
            name: 'Total<br />vs. Exp.',
            field: 'overturns_vs_exp_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-chal`,
            fieldClass: `tr-data align-center border-left abs-summary-chal`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            highlightDynamic: true,
            toolTip: `Net overturns more<br />than expected by<br />a team seeing identical<br />pitches (incl. challenges<br />by and against team)`,
        },
        {
            name: 'Chal',
            field: 'chal_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-chal`,
            fieldClass: `tr-data align-center border-left abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Total challenges<br />by team (both<br />as batter and fielder)`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won',
            field: 'overturns_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-chal`,
            fieldClass: `tr-data align-center dashed-left abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Total overturns<br />on challenges<br />by team (both<br />as batter and fielder)`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Loss',
            field: 'loss_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-summary-chal`,
            fieldClass: `tr-data align-center abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Total calls confirmed<br />on challenges<br />by team (both<br />as batter and fielder)`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won%',
            field: 'rate_overturns_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-chal`,
            fieldClass: `tr-data align-center dashed-left abs-summary-chal`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of calls overturned<br />on challenges by<br />team (both batters<br />and fielders)`,
        },
        {
            name: 'Gained<br />vs. Exp.',
            field: 'net_chal_gained_runs_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-runs`,
            fieldClass: `tr-data align-center border-left abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            highlightDynamic: true,
            toolTip: `Actual run value on<br />overturns on challenges by<br />team minus expected`,
        },
        {
            name: 'Chal.',
            field: 'chal_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-runs`,
            fieldClass: `tr-data align-center border-left abs-summary-runs`,
            defaultSortDir: 'desc',
            toolTip: `Total challenges<br />by team (both<br />as batter and fielder)`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Runs<br />Gained',
            field: 'n_chal_runs_gained_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-runs`,
            fieldClass: `tr-data align-center dashed-left abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Total run value<br />on overturns<br />on challenges<br />by team (both<br />as batter and fielder)`,
        },
        {
            name: 'Runs<br />Gained<br />Per Chal.',
            field: 'runs_gained_per_chal_total',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-summary-runs`,
            fieldClass: `tr-data align-center abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'runs_gained_per_chal'),
            toolTip: `Total run value<br />on confirms<br />per challenge<br />by team (both<br />as batter and fielder)`,
        },
        {
            name: 'Total<br />vs. Exp.',
            field: 'overturns_vs_exp_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left-bold abs-summary-chal`,
            fieldClass: `tr-data align-center border-left-bold abs-summary-chal`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            highlightDynamic: true,
            toolTip: `Net overturns more<br />than expected by<br />a batting team seeing<br />identical pitches (incl.<br />challenges by and against team)`,
        },
        {
            name: 'Gained<br />vs. Exp.',
            field: 'net_chal_gained_runs_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left-bold abs-summary-runs`,
            fieldClass: `tr-data align-center border-left-bold abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            highlightDynamic: true,
            toolTip: `Actual run value on<br />overturns on challenges by<br />batters minus expected`,
        },
        {
            name: 'Net<br />Ovr.<br />For',
            field: 'net_net_chal_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-chal`,
            fieldClass: `tr-data align-center border-left abs-summary-chal`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Net overturns more<br />than expected by<br />a batting team seeing<br />identical pitches (only<br />challenges by team)`,
        },
        {
            name: 'Net<br />Ovr.<br />Against',
            field: 'net_net_chal_against_proxy_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-summary-chal`,
            fieldClass: `tr-data align-center border-right abs-summary-chal`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val, row) =>
                guff(row.net_net_chal_against_off, 'exp_abs_chals'),
            toolTip: `Net overturns more<br />than expected by<br />a batting team seeing<br />identical pitches (only<br />challenges against team)`,
        },
        {
            name: 'Chal',
            field: 'n_challenges_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-chal`,
            fieldClass: `tr-data align-center border-left abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Challenges made<br />by team batters`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won',
            field: 'n_overturns_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-chal`,
            fieldClass: `tr-data align-center dashed-left abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Calls overturned<br />on challenges<br />by team batters`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Lost',
            field: 'n_fails_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-right abs-summary-chal`,
            fieldClass: `tr-data align-center dashed-right abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Calls confirmed<br />on challenges<br />by team batters`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won%',
            field: 'rate_overturns_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-summary-chal`,
            fieldClass: `tr-data align-center abs-summary-chal`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of calls overturned<br />on challenges by<br />team batters`,
        },
        {
            name: 'Chal.',
            field: 'n_challenges_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-runs`,
            fieldClass: `tr-data align-center border-left abs-summary-runs`,
            defaultSortDir: 'desc',
            toolTip: `Run value on<br />challenges made<br />by team batters`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Runs<br />Gained',
            field: 'n_chal_runs_gained_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-runs`,
            fieldClass: `tr-data align-center dashed-left abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Run value on<br />calls overturned<br />on challenges<br />by team batters`,
        },
        {
            name: 'Runs<br />Gained<br />Per Chal.',
            field: 'runs_gained_per_chal_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-right abs-summary-runs`,
            fieldClass: `tr-data align-center dashed-right abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'runs_gained_per_chal'),
            toolTip: `Run value on<br />calls overturned<br />per challenge<br />by team batters`,
        },
        {
            name: 'xRuns<br />Gained',
            field: 'exp_chal_runs_gained_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-runs`,
            fieldClass: `tr-data align-center border-left abs-summary-runs`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Expected run value<br />on challenges<br />by team batters`,
        },
        {
            name: 'xRuns<br />Gained<br />Per Chal.',
            field: 'exp_runs_gained_per_chal_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-summary-runs`,
            fieldClass: `tr-data align-center border-right abs-summary-runs`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val, row) =>
                guff(row.net_net_runs_against_off, 'runs_gained_per_chal'),
            toolTip: `Expected run value<br />per challenge<br />by team batters`,
        },
        {
            name: '-K<br />Flip',
            field: 'n_strikeouts_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
            toolTip: `Strikeouts<br />eliminated on<br />challenges by<br />team batters`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: '+BB<br />Flip',
            field: 'n_walks_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'desc',
            toolTip: `Walks gained on<br />challenges by<br />team batters`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Chal.<br />Rate',
            field: 'rate_challenges_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom`,
            fieldClass: `tr-data align-center`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `% of "challengeable"<br />pitches challenged<br />by team batters`,
        },
        {
            name: 'Rate<br />vs. Exp.',
            field: 'exp_rate_challenges_diff_off',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-left border-right`,
            defaultSortDir: 'desc',
            displayFn: (val) =>
                guff(val, 'abs_chal_rate_percent', {
                    isDiff: true,
                    ignoreExtremaHandling: true,
                }),
            expanded: true,
            highlightDynamic: true,
            toolTip: `Actual - expected %<br />of "challengeable"<br />pitches challenged<br />by team batters`,
        },
        {
            name: 'Total<br />vs. Exp.',
            field: 'overturns_vs_exp_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left-bold abs-summary-chal`,
            fieldClass: `tr-data align-center border-left-bold abs-summary-chal`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            highlightDynamic: true,
            toolTip: `Net overturns more<br />than expected by<br />a fielding team seeing<br />identical pitches (incl.<br />challenges by and against team)`,
        },
        {
            name: 'Gained<br />vs. Exp.',
            field: 'net_chal_gained_runs_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left-bold abs-summary-runs`,
            fieldClass: `tr-data align-center border-left-bold abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            highlightDynamic: true,
            toolTip: `Actual run value on<br />overturns on challenges by<br />fielders minus expected`,
        },
        {
            name: 'Net<br />Ovr.<br />For',
            field: 'net_net_chal_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-chal`,
            fieldClass: `tr-data align-center border-left abs-summary-chal`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'exp_abs_chals'),
            toolTip: `Net overturns more<br />than expected by<br />a fielding team seeing<br />identical pitches (only<br />challenges by team)`,
        },
        {
            name: 'Net<br />Ovr.<br />Against',
            field: 'net_net_chal_against_proxy_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-summary-chal`,
            fieldClass: `tr-data align-center border-right abs-summary-chal`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val, row) =>
                guff(row.net_net_chal_against_def, 'exp_abs_chals'),
            toolTip: `Net overturns more<br />than expected by<br />a fielding team seeing<br />identical pitches (only<br />challenges against team)`,
        },
        {
            name: 'Chal',
            field: 'n_challenges_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-chal`,
            fieldClass: `tr-data align-center border-left abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Challenges made<br />by team fielders`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won',
            field: 'n_overturns_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-chal`,
            fieldClass: `tr-data align-center dashed-left abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Calls overturned<br />on challenges<br />by team fielders`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Lost',
            field: 'n_fails_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-right abs-summary-chal`,
            fieldClass: `tr-data align-center dashed-right abs-summary-chal`,
            defaultSortDir: 'desc',
            toolTip: `Calls confirmed<br />on challenges<br />by team fielders`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Won%',
            field: 'rate_overturns_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom abs-summary-chal`,
            fieldClass: `tr-data align-center abs-summary-chal`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_percent'),
            toolTip: `% of calls overturned<br />on challenges by<br />team fielders`,
        },
        {
            name: 'Chal.',
            field: 'n_challenges_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-runs`,
            fieldClass: `tr-data align-center border-left abs-summary-runs`,
            defaultSortDir: 'desc',
            toolTip: `Run value on<br />challenges made<br />by team fielders`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Runs<br />Gained',
            field: 'n_chal_runs_gained_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-left abs-summary-runs`,
            fieldClass: `tr-data align-center dashed-left abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Run value on<br />calls overturned<br />on challenges<br />by team fielders`,
        },
        {
            name: 'Runs<br />Gained<br />Per Chal.',
            field: 'runs_gained_per_chal_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom dashed-right abs-summary-runs`,
            fieldClass: `tr-data align-center dashed-right abs-summary-runs`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'runs_gained_per_chal'),
            toolTip: `Run value on<br />calls confirmed<br />per challenge<br />by team fielders`,
        },
        {
            name: 'xRuns<br />Gained',
            field: 'exp_chal_runs_gained_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left abs-summary-runs`,
            fieldClass: `tr-data align-center border-left abs-summary-runs`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) => guff(val, 'abs_chal_runs'),
            toolTip: `Expected run value<br />on challenges<br />by team fielders`,
        },
        {
            name: 'xRuns<br />Gained<br />Per Chal.',
            field: 'exp_runs_gained_per_chal_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right abs-summary-runs`,
            fieldClass: `tr-data align-center border-right abs-summary-runs`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val, row) =>
                guff(row.net_net_runs_against_off, 'runs_gained_per_chal'),
            toolTip: `Expected run value<br />per challenge<br />by team fielders`,
        },
        {
            name: '+K<br />Flip',
            field: 'n_strikeouts_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-left`,
            fieldClass: `tr-data align-center border-left`,
            defaultSortDir: 'desc',
            toolTip: `Strikeouts<br />gained on<br />challenges by<br />team fielders`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: '-BB<br />Flip',
            field: 'n_walks_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right`,
            fieldClass: `tr-data align-center border-right`,
            defaultSortDir: 'desc',
            toolTip: `Walks eliminated on<br />challenges by<br />team fielders`,
            displayFn: (val) => guff(val, 'abs_challenges'),
        },
        {
            name: 'Chal.<br />Rate',
            field: 'rate_challenges_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom ${
                isExpanded ? '' : 'border-right-bold'
            }`,
            fieldClass: `tr-data align-center ${
                isExpanded ? '' : 'border-right-bold'
            }`,
            defaultSortDir: 'desc',
            displayFn: (val) => guff(val, 'abs_chal_rate_percent'),
            toolTip: `% of "challengeable"<br />pitches challenged<br />by team fielders`,
        },
        {
            name: 'Rate<br />vs. Exp.',
            field: 'exp_rate_challenges_diff_def',
            type: 'number',
            headerClass: `th-component-header align-center th-vertical-bottom border-right-bold`,
            fieldClass: `tr-data align-center border-left border-right-bold`,
            defaultSortDir: 'desc',
            expanded: true,
            displayFn: (val) =>
                guff(val, 'abs_chal_rate_percent', {
                    isDiff: true,
                    ignoreExtremaHandling: true,
                }),
            highlightDynamic: true,
            toolTip: `Actual - expected %<br />of "challengeable"<br />pitches challenged<br />by team fielders`,
        },
        {
            name: '',
            field: 'player_name',
            headerClass:
                'th-component-header align-center th-vertical-bottom cursor-initial',
            fieldClass: 'tr-data',
            sortable: false,
            displayFn: () =>
                `
                    <span data-video-icon style="inline-block; padding: 2px; border-radius: 50%; border: 2px solid #c2c2c2;">
                        <img src="/site-core/images/video.png" style="width: 18px; cursor: pointer;">
                    </span>
                    `,
        },
    ]

    return headersSummary
}

export function getSecondaryHeadersSummary(
    selectedCount: Count,
    isExpanded: boolean,
    extraCols: number,
    level: string
) {
    const modeSelector = `
    <div class="form-group">
        <div class="savant-select">
            <select id="${countSelectorId}">
                <option value="chal" ${
                    selectedCount === 'chal' ? 'selected' : ''
                }>
                    # Challenges
                </option>
                <option value="runs" ${
                    selectedCount === 'runs' ? 'selected' : ''
                }>
                    Run Values
                </option>
            </select>
        </div>
        <button id="btn-expected" class="btn btn-default">
            ${isExpanded ? 'Hide' : 'Show'} Expected Breakdown
        </button>
    </div>
    `

    const mlbOffset = level === 'mlb' ? -1 : 0
    const countOffset = selectedCount === 'runs' ? -1 : 0

    return [
        {
            name: modeSelector,
            colspan: 9 + extraCols + mlbOffset + countOffset,
            headerClass: 'th-title-header',
        },
        {
            name: 'As Batters',
            colspan: isExpanded ? 11 + countOffset : 8 + countOffset,
            headerClass: 'th-title-header border-left-bold',
        },
        {
            name: 'As Fielders (Catchers + Pitchers)',
            colspan: isExpanded ? 11 + countOffset : 8 + countOffset,
            headerClass: 'th-title-header border-left-bold border-right-bold',
        },
    ]
}

export function getSecondaryHeaders(
    selectedMode: Mode,
    selectedCount: Count,
    isExpanded: boolean,
    extraCols: number,
    level: string
) {
    const modeSelector = `
    <div class="form-group">
        <div class="savant-select">
            <select id="${countSelectorId}">
                <option value="chal" ${
                    selectedCount === 'chal' ? 'selected' : ''
                }>
                    # Challenges
                </option>
                <option value="runs" ${
                    selectedCount === 'runs' ? 'selected' : ''
                }>
                    Run Values
                </option>
            </select>
        </div>
        <div class="savant-select">
            <select id="${selectorId}">
                <option value="for" ${selectedMode === 'for' ? 'selected' : ''}>
                    As Challenger
                </option>
                <option value="against" ${
                    selectedMode === 'against' ? 'selected' : ''
                }>
                    As Challenged (Opp. POV)
                </option>
            </select>
        </div>
        <button id="btn-expected" class="btn btn-default">
            ${isExpanded ? 'Hide' : 'Show'} Expected Breakdown
        </button>
    </div>
    `

    const mlbOffset = level === 'mlb' ? -2 : 0
    let runsOffset = 0

    if (selectedCount === 'runs') {
        runsOffset -= 1

        if (isExpanded) {
            runsOffset -= 1
        }
    }

    return [
        {
            name: '',
            colspan: 5 + extraCols + mlbOffset,
            headerClass: 'th-name-header',
        },
        {
            name: 'Overturns<br />vs. Expected',
            colspan: isExpanded && selectedCount === 'chal' ? 3 : 1,
            headerClass: 'th-title-header border-left border-right',
        },
        {
            name: 'Runs<br />vs. Expected',
            colspan:
                isExpanded && selectedCount === 'runs' && isPlayerType ? 3 : 1,
            headerClass: 'th-title-header border-left border-right',
        },
        {
            name: modeSelector,
            colspan: isExpanded ? 19 + runsOffset : 13 + runsOffset,
            headerClass: 'th-title-header border-left border-right',
        },
    ]
}
